import * as Automerge from "@automerge/automerge/slim";
import * as AutomergeRepo from "@automerge/automerge-repo/slim";
import { ModuleWatcher } from "@inkandswitch/patchwork-filesystem";
import { importAutomergePackageViaWorker } from "@inkandswitch/patchwork-bootloader/module-loader";
import { registerPatchworkViewElement } from "@inkandswitch/patchwork-elements";
import { registerRepoProviderElement } from "@inkandswitch/patchwork-providers";
import { createDocOfDatatype2, getRegistry, registerPlugins, resolveAccountHandle, unregisterPlugins, } from "@inkandswitch/patchwork-plugins";
import * as plugins from "@inkandswitch/patchwork-plugins";
import setupServiceWorker from "@inkandswitch/patchwork-bootloader";
import debug from "debug";
import { createRepo, initWasm } from "./repo.js";
import { createRouter } from "./router.js";
import { createDefaultAccount } from "./createAccount.js";
const log = debug("patchwork:setup");
// ── Setup ────────────────────────────────────────────────────────────────
let setupCalled = false;
export function setup(options = {}) {
    if (setupCalled) {
        throw new Error("patchwork.setup: already called — setup owns page-wide state and may run only once");
    }
    setupCalled = true;
    const done = doSetup(options);
    void done.then(() => log("patchwork setup complete"));
    const timeout = options.timeout ?? 30_000;
    if (timeout === false)
        return done;
    let timer;
    const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`patchwork.setup: boot did not finish within ${timeout}ms`)), timeout);
    });
    done.then(() => clearTimeout(timer), () => clearTimeout(timer));
    return Promise.race([done, deadline]);
}
export default setup;
async function doSetup(options) {
    const siteTitle = options.title ??
        (typeof __SITE_TITLE__ !== "undefined" ? __SITE_TITLE__ : "Patchwork");
    const moduleSources = resolveDefaultModules(options);
    const routing = options.routing ?? "hash";
    log("booting", options);
    if (!options.repo)
        await initWasm();
    const sw = await setupServiceWorker();
    if (!sw)
        throw new Error("Failed to set up service worker");
    log("workers ready");
    let hive;
    let repo;
    let signerIdentity;
    if (options.repo) {
        log("using provided Repo");
        repo = options.repo;
        hive = options.hive;
    }
    else {
        ({ repo, hive, signerIdentity } = await createRepo());
    }
    // Dev-console / tool-runtime globals (e2e and loaded tools read these). The
    // `window.patchwork` handle is deliberately not set here — the caller does
    // `window.patchwork = await setup(...)`.
    window.repo = repo;
    window.Automerge = Automerge;
    window.AutomergeRepo =
        AutomergeRepo;
    if (hive)
        window.hive = hive;
    hive?.networkAdapter?.syncKeyhive?.();
    registerRepoProviderElement(repo);
    const rootElementId = options.rootElementId ?? "root";
    const rootElement = document.getElementById(rootElementId);
    if (!rootElement) {
        throw new Error(`patchwork.setup: no element with id="${rootElementId}"`);
    }
    // `<repo-provider>` sits above the root and answers `repo:handle-descriptor`
    // for any view outside a remapper, resolving to the requested url unchanged.
    // Generated site html already declares it around the root; wrap only
    // hand-written markup that lacks one.
    if (!rootElement.closest("repo-provider")) {
        const repoProvider = document.createElement("repo-provider");
        rootElement.parentElement.insertBefore(repoProvider, rootElement);
        repoProvider.appendChild(rootElement);
    }
    registerPatchworkViewElement({ hive, repo });
    // Started with the site bundle alone so resolveAccountHandle has something to
    // await on — the `account` datatype lives there. The user's own
    // module-settings URL is added lazily once it appears on the account doc.
    const moduleWatcher = new ModuleWatcher(repo, nameSources(moduleSources), onModuleLoaded, unregisterPlugins, 
    // Discover an Automerge package's plugin descriptors off the main thread;
    // each plugin's load() re-imports the package (at heads) on this thread.
    options.importModulesInWorker === false
        ? undefined
        : importAutomergePackageViaWorker);
    const accountDocHandle = (await resolveAccountHandle(repo, {
        storageKey: options.accountKey ?? "patchworkAccountURL",
        hive,
        createAccount: options.createAccount ?? createDefaultAccount,
    }));
    wireModuleSettings(accountDocHandle, moduleWatcher);
    const toolsLoaded = moduleWatcher.doneLoading.then(() => log("doneLoading, tools registered:", getRegistry("patchwork:tool")
        .all()
        .map((t) => t.id)), (err) => console.error("doneLoading rejected:", err));
    let router;
    if (routing !== false) {
        rootElement.style.visibility = "hidden";
        await toolsLoaded;
        router = createRouter({
            rootElement,
            repo,
            accountDocHandle,
            siteTitle,
            frameToolId: options.frameToolId,
        });
    }
    installReveal(rootElement, router, toolsLoaded);
    return {
        repo,
        hive,
        account: accountDocHandle,
        signer: signerIdentity,
        packages: moduleWatcher,
        plugins,
        sw: {
            connectClassicSync: sw.connectClassicSync,
            subscribeSyncState: (documentId, listener) => subscribeSyncState(repo, documentId, listener),
        },
        async create(type, init) {
            const datatype = await getRegistry("patchwork:datatype").load(type);
            if (!datatype) {
                throw new Error(`patchwork.create: no datatype registered for "${type}"`);
            }
            return createDocOfDatatype2(datatype, repo, init, hive);
        },
        open(url, openOptions = {}) {
            rootElement.dispatchEvent(new CustomEvent("patchwork:open-document", {
                detail: {
                    url,
                    toolId: openOptions.tool,
                    type: openOptions.type,
                    title: openOptions.title,
                },
            }));
        },
        find(url) {
            return repo.find(url);
        },
    };
}
// ── Reveal / mount ───────────────────────────────────────────────────────
function installReveal(rootElement, router, toolsLoaded) {
    let revealed = false;
    const reveal = () => {
        if (revealed)
            return;
        revealed = true;
        rootElement.style.visibility = "visible";
    };
    rootElement.addEventListener("patchwork:mounted", (event) => {
        if (event.target !== rootElement)
            return;
        log("root element mounted");
        if (router) {
            void router.route();
            // Deep-links into tools that were still loading get routed again once
            // every module has registered.
            void toolsLoaded.then(() => router.route());
        }
        reveal();
    });
    // If nothing ever mounts, reveal anyway so the user sees something rather
    // than a blank page.
    setTimeout(reveal, 12_000);
}
// ── Module sources ───────────────────────────────────────────────────────
/**
 * The site's module-list sources. The site owns any dev overrides and passes
 * the result in as `packageListURL`.
 */
function resolveDefaultModules(options) {
    const configured = options.packageListURL ?? [];
    const builtin = (Array.isArray(configured) ? configured : [configured]).filter(Boolean);
    if (builtin.length === 0) {
        throw new Error("patchwork.setup: no default module sources configured (set `packageListURL`)");
    }
    return builtin;
}
/**
 * Name the sources for the ModuleWatcher. The first keeps the canonical
 * `system` name; the rest get suffixed. None may be `user`, which is reserved
 * for the per-account settings doc and has branch-override precedence.
 */
function nameSources(sources) {
    return Object.fromEntries(sources.map((source, i) => [i === 0 ? "system" : `system-${i}`, source]));
}
function onModuleLoaded(name, mod) {
    if (!Array.isArray(mod.plugins)) {
        console.warn(`module ${name} has no plugins array`, Object.keys(mod));
        return;
    }
    log(`registering ${mod.plugins.length} plugin(s) from ${name}`, mod.plugins.map((p) => `${p.type}:${p.id}`));
    registerPlugins(mod.plugins, name);
}
/**
 * Feed the account's module settings document to the ModuleWatcher.
 */
function wireModuleSettings(accountDocHandle, moduleWatcher) {
    const wire = () => {
        const url = accountDocHandle.doc()?.moduleSettingsUrl;
        if (!url)
            return;
        void moduleWatcher.addUrl("user", url);
        accountDocHandle.off("change", wire);
    };
    wire();
    if (!accountDocHandle.doc()?.moduleSettingsUrl) {
        accountDocHandle.on("change", wire);
    }
}
// The tab's own Repo hears the server's heads directly, so this is a filter
// over its `subduction-remote-heads` event. The current value is replayed
// from the handle's sync info, when the server has reported any.
function subscribeSyncState(repo, documentId, listener) {
    const onHeads = (payload) => {
        if (payload.documentId !== documentId)
            return;
        listener({
            type: "sync-state",
            documentId,
            storageId: payload.storageId,
            heads: [...payload.heads],
            timestamp: payload.timestamp,
        });
    };
    repo.on("subduction-remote-heads", onHeads);
    void (async () => {
        const handle = repo.handles[documentId];
        if (!handle)
            return;
        for (const storageId of await repo.connectedSubductionPeerIds()) {
            const info = handle.getSyncInfo(storageId);
            if (info) {
                onHeads({
                    documentId,
                    storageId,
                    heads: info.lastHeads,
                    timestamp: info.lastSyncTimestamp,
                });
            }
        }
    })();
    return () => repo.off("subduction-remote-heads", onHeads);
}
// ── Named exports ────────────────────────────────────────────────────────
export { createRepo, initWasm } from "./repo.js";
export { createRouter } from "./router.js";
export { showLoadingAnimation, hideLoadingAnimation, showErrorScreen, } from "./loading.js";
